// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type Holding } from '@shared/data-access/interfaces/api/ai/holding';
import { type PortfolioStrategy } from '@shared/data-access/interfaces/api/ai/portfolio-strategy';
import { type KpiBoxesProps } from '@shared/ui-kit/components';
import { portfolioMultiAssetApi } from '@shared/data-access/api/ai/portfolio-multiasset-api';
import { useMemo } from 'react';
import { type TargetAsset } from '@shared/data-access/interfaces/api/ai/target-strategy';
import { useTranslation } from 'react-i18next';

export interface UseMultiAssetBacktestArgs {
  holdings: Holding[];
  benchmark: TargetAsset[];
  hist: number;
  skip?: boolean;
}

export const useMultiAssetBacktest = ({
  hist,
  holdings,
  benchmark,
  skip = false,
}: UseMultiAssetBacktestArgs) => {
  const { t, i18n } = useTranslation();

  /* istanbul ignore next */
  const portfolioMultiAssetArgs = useMemo<{
    params: { hist_w: number };
    payload: PortfolioStrategy;
  }>(
    () => ({
      params: {
        hist_w: hist,
      },
      payload: {
        benchmark: { target_asset: benchmark },
        holdings: holdings,
      },
    }),
    [hist, benchmark, holdings],
  );

  const {
    isLoading: isLoadingAnalysis,
    isFetching: isFetchingAnalysis,
    isError: isErrorAnalysis,
    data: analysis,
    refetch: refetchAnalysis,
  } = portfolioMultiAssetApi.usePortfolioMultiAssetAnalysisQuery(
    portfolioMultiAssetArgs,
    { skip: skip },
  );

  const {
    isLoading: isLoadingBacktest,
    isFetching: isFetchingBacktest,
    isError: isErrorBacktest,
    data: backtest,
    refetch: refetchBacktest,
  } = portfolioMultiAssetApi.usePortfolioMultiAssetBacktestQuery(
    portfolioMultiAssetArgs,
    { skip: skip },
  );

  const isLoadingBacktesting = useMemo(
    () => [isLoadingAnalysis, isLoadingBacktest].some(Boolean),
    [isLoadingAnalysis, isLoadingBacktest],
  );

  const isFetchingBacktesting = useMemo(
    () => [isFetchingAnalysis, isFetchingBacktest].some(Boolean),
    [isFetchingAnalysis, isFetchingBacktest],
  );

  const isError = useMemo(
    () => [isErrorAnalysis, isErrorBacktest].some(Boolean),
    [isErrorAnalysis, isErrorBacktest],
  );

  const kpis = useMemo<KpiBoxesProps[]>(() => {
    const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';
    const numberFormat = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      trailingZeroDisplay: 'stripIfInteger',
      useGrouping: true,
    });

    return [
      {
        text: t('common.annualized-returns', { ns: 'common' }) as string,
        value: numberFormat.format(analysis?.returns?.CAGR ?? 0),
        benchmarkScore: numberFormat.format(analysis?.returns?.bnch_CAGR ?? 0),
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        text: t('common.volatility', { ns: 'common' }) as string,
        value: numberFormat.format(analysis?.risk.volatility ?? 0),
        popupName: 'kpis.annualizedVolatility',
        benchmarkScore: numberFormat.format(analysis?.risk.bnch_volatility ?? 0),
        icon: 'Activity',
        color: 'danger',
      },
      {
        text: t('common.sharpe-ratio', { ns: 'common' }) as string,
        value: numberFormat
          .format((analysis?.risk.sharpe ?? 0) / 100)
          .replace('%', ''),
        popupName: 'kpis.sharpeRatio',
        benchmarkScore: numberFormat
          .format((analysis?.risk.bnch_sharpe ?? 0) / 100)
          .replace('%', ''),
        icon: 'Target',
        color: 'main',
      },
      {
        text: t('common.max-drawdown', { ns: 'common' }) as string,
        value: numberFormat.format(analysis?.risk.max_drawdown ?? 0),
        popupName: 'kpis.maxDrawdown',
        benchmarkScore: numberFormat.format(analysis?.risk.bnch_max_drawdown ?? 0),
        icon: 'TrendingDown',
        color: 'danger',
      },
    ];
  }, [
    analysis?.returns?.CAGR,
    analysis?.returns?.bnch_CAGR,
    analysis?.risk.bnch_max_drawdown,
    analysis?.risk.bnch_sharpe,
    analysis?.risk.bnch_volatility,
    analysis?.risk.max_drawdown,
    analysis?.risk.sharpe,
    analysis?.risk.volatility,
    i18n.language,
    t,
  ]);

  const returns = useMemo(
    () =>
      /* istanbul ignore next */
      analysis?.returns
        ? [
            {
              label: t('common.1m', { ns: 'common' }) as string,
              portfolio: analysis?.returns?.returns_1M ?? 0,
              benchmark: analysis?.returns?.bnch_returns_1M ?? 0,
            },
            {
              label: t('common.3m', { ns: 'common' }) as string,
              portfolio: analysis?.returns?.returns_3M ?? 0,
              benchmark: analysis?.returns?.bnch_returns_3M ?? 0,
            },
            {
              label: t('common.6m', { ns: 'common' }) as string,
              portfolio: analysis?.returns?.returns_6M ?? 0,
              benchmark: analysis?.returns?.bnch_returns_6M ?? 0,
            },

            {
              label: t('common.3-years', { ns: 'common' }) as string,
              portfolio: analysis?.returns?.returns_3Y ?? 0,
              benchmark: analysis?.returns?.bnch_returns_3Y ?? 0,
            },
            {
              label: t('common.5-years', { ns: 'common' }) as string,
              portfolio: analysis?.returns?.returns_5Y ?? 0,
              benchmark: analysis?.returns?.bnch_returns_5Y ?? 0,
            },
          ]
        : [],
    [analysis?.returns, t],
  );

  return {
    isLoadingBacktesting,
    isFetchingBacktesting,
    analysis,
    backtest,
    kpis,
    returns,
    isError,
    refetchAnalysis,
    refetchBacktest,
  };
};
